import React, { useState, useEffect, useRef } from 'react';
import {
  Edit3, MapPin, Mail, Star, Bookmark, CheckCircle,
  Loader, LogOut, ChevronRight, User,
  Phone, Trash2, FileText, Shield, Camera,
  Building2, Target, GraduationCap, AlertCircle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { fetchProfile, updateProfile, uploadAvatar, deleteAccount } from '../services/api';
import LocationInput from '../components/LocationInput';
import ApplicantProfile from '../components/ApplicantProfile';
import {
  JOB_TYPE_LABELS, BRANCHE_LABELS, DAYTIME_LABELS, labelOf, weekdaysLabel, jobTitle,
} from '../constants/jobOptions';
import './ProfilePage.css';

const DEFAULT_PROFILE = { bio: '', location: '', location_id: null, phone: '', avatar: '' };

const range = (min, max, unit) =>
  (min != null || max != null ? `${min ?? '…'}–${max ?? '…'} ${unit}` : null);

// Gespeicherte Wünsche (Form der /jobs-Query-Parameter) als lesbare Stichpunkte
const wishSummary = (p = {}) => [
  ...(p.job_type || []).map(s => labelOf(JOB_TYPE_LABELS, s)),
  ...(p.branche || []).map(s => labelOf(BRANCHE_LABELS, s)),
  (p.weekdays || []).length ? weekdaysLabel(p.weekdays) : null,
  ...(p.daytime || []).map(s => labelOf(DAYTIME_LABELS, s)),
  range(p.days_per_week_min, p.days_per_week_max, 'Tage/Woche'),
  range(p.hours_per_week_min, p.hours_per_week_max, 'Std./Woche'),
  p.pay_per_hour_min != null && `ab ${p.pay_per_hour_min} €/Std.`,
  p.start_date && `ab ${new Date(p.start_date).toLocaleDateString('de-DE')}`,
  p.no_experience_required && 'Keine Vorkenntnisse nötig',
  p.drivers_license_required === false && 'Kein Führerschein nötig',
  p.remote_possible && 'Homeoffice möglich',
].filter(Boolean);

export default function ProfilePage() {
  const { user, openAuth, logout, savedJobs, appliedJobs } = useApp();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [profileData, setProfileData] = useState(DEFAULT_PROFILE);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);

  useEffect(() => {
    if (!user) { setProfileLoading(false); return; }
    setProfileLoading(true);
    fetchProfile()
      .then(({ data }) => setProfileData({
        bio: data.bio || data.company_description || '',
        location: data.location || data.youth_location || data.company_location || '',
        location_id: data.location_id ?? null,
        phone: data.phone || '',
        avatar: data.avatar || data.avatar_url || data.logo_url || '',
      }))
      .catch(() => {})
      .finally(() => setProfileLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) {
    return (
      <div className="profile-gate">
        <div className="profile-gate__content">
          <div className="profile-gate__avatar"><User size={56} strokeWidth={1.2} /></div>
          <h2>Profil erstellen</h2>
          <p>Melde dich an, um dein Profil zu sehen und zu verwalten.</p>
          <button className="profile-gate__btn profile-gate__btn--primary" onClick={() => openAuth('register')}>
            Jetzt registrieren
          </button>
          <button className="profile-gate__btn profile-gate__btn--ghost" onClick={() => openAuth('login')}>
            Anmelden
          </button>
        </div>
      </div>
    );
  }

  const isYouth = user.user_type === 'youth';

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    try {
      const { bio, location, location_id, phone } = profileData;
      // Bewerber bearbeiten hier nur Kontakt; "Über mich" & Co. laufen über /profile/edit
      await updateProfile(isYouth ? { location, location_id, phone } : { bio, location, phone });
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Profil konnte nicht gespeichert werden.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await deleteAccount();
      logout();
      navigate('/');
    } catch {
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarLoading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      setProfileData(p => ({ ...p, avatar: base64 }));
      try {
        await uploadAvatar(base64);
      } catch {
        // stored locally in state; endpoint may not exist yet
      } finally {
        setAvatarLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Arbeitgeber: Fortschrittsbalken; Bewerber: profile_complete vom Backend
  const completionFields = [profileData.bio, profileData.location, profileData.phone];
  const completion = Math.round((completionFields.filter(Boolean).length / completionFields.length) * 100);
  const wishes = wishSummary(user.preferences || {});

  const firstLetter = (user.first_name || user.name || '?').charAt(0).toUpperCase();
  const displayName = user.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user.name || user.email;
  const displayAvatar = profileData.avatar || user?.avatar_url || null;

  return (
    <div className="profile-page">
      {/* Hero header */}
      <div className="profile-hero">
        <div className="profile-avatar-wrap">
          <div className="profile-avatar">
            {displayAvatar
              ? <img src={displayAvatar} alt="Profilbild" />
              : firstLetter}
            {avatarLoading && <div className="profile-avatar-loader"><Loader size={18} className="spin" /></div>}
          </div>
          {editing && (
            <>
              <button
                className="profile-avatar-edit"
                onClick={() => fileInputRef.current?.click()}
                title="Foto ändern"
              >
                <Camera size={14} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </>
          )}
        </div>
        <h1 className="profile-name">{displayName}</h1>
        <p className="profile-role">
          {user.user_type === 'employer'
            ? <><Building2 size={14} /> Arbeitgeber</>
            : <><Target size={14} /> Jobsuchend · Offen für Angebote</>
          }
        </p>
        {profileData.location && (
          <p className="profile-location"><MapPin size={13} /> {profileData.location}</p>
        )}

        {!profileLoading && !isYouth && completion < 100 && (
          <div className="profile-completion">
            <div className="profile-completion__bar">
              <div className="profile-completion__fill" style={{ width: `${completion}%` }} />
            </div>
            <span className="profile-completion__label">Profil {completion}% vollständig</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        {[
          { id: 'profile', label: 'Profil' },
          { id: 'saved', label: `Gespeichert (${savedJobs.length})` },
          { id: 'applied', label: `Bewerbungen (${appliedJobs.length})` },
        ].map(tab => (
          <button
            key={tab.id}
            className={`profile-tab${activeTab === tab.id ? ' profile-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Profile tab */}
      {activeTab === 'profile' && (
        <div className="profile-content">
          {saveError && <div className="profile-save-error">{saveError}</div>}

          {profileLoading ? (
            <div className="profile-skel">
              <div className="skel profile-skel__avatar" />
              <div className="skel profile-skel__line" style={{ width: '58%' }} />
              <div className="skel profile-skel__line" style={{ width: '43%' }} />
              <div className="skel profile-skel__line profile-skel__line--sm" style={{ width: '52%' }} />
              <div className="skel profile-skel__line profile-skel__line--sm" style={{ width: '38%' }} />
            </div>
          ) : (
            <>
              {/* Bewerber: dieselben Screens wie im Setup (/profile/edit/:section) */}
              {isYouth && user.profile_complete === false && (
                <div className="profile-incomplete">
                  <AlertCircle size={20} className="profile-incomplete__icon" />
                  <div className="profile-incomplete__text">
                    <strong>Dein Profil ist noch unvollständig</strong>
                    <span>Arbeitgeber sehen bei deiner Bewerbung Schule, Erfahrung und „Über mich“.</span>
                  </div>
                  <button
                    className="profile-incomplete__btn"
                    onClick={() => navigate(`/profile/edit/${user.school_status ? 'bio' : 'about'}`)}
                  >
                    Profil vervollständigen
                  </button>
                </div>
              )}

              {isYouth && (
                <>
                  <div className="profile-section">
                    <div className="profile-section__header">
                      <div className="profile-section__title"><GraduationCap size={16} /> Erzähl uns von dir</div>
                      <button className="profile-section__edit" onClick={() => navigate('/profile/edit/about')}>
                        <Edit3 size={14} /> Bearbeiten
                      </button>
                    </div>
                    <ApplicantProfile profile={user} showBio={false} />
                  </div>

                  <div className="profile-section">
                    <div className="profile-section__header">
                      <div className="profile-section__title"><Star size={16} /> Über mich</div>
                      <button className="profile-section__edit" onClick={() => navigate('/profile/edit/bio')}>
                        <Edit3 size={14} /> Bearbeiten
                      </button>
                    </div>
                    <p className="profile-text">
                      {user.bio || <span className="profile-empty-text">Noch keine Beschreibung hinzugefügt.</span>}
                    </p>
                  </div>

                  <div className="profile-section">
                    <div className="profile-section__header">
                      <div className="profile-section__title"><Target size={16} /> Wünsche</div>
                      <button className="profile-section__edit" onClick={() => navigate('/profile/edit/wishes')}>
                        <Edit3 size={14} /> Bearbeiten
                      </button>
                    </div>
                    {wishes.length > 0 ? (
                      <div className="profile-skills">
                        {wishes.map(w => <span key={w} className="profile-skill-tag">{w}</span>)}
                      </div>
                    ) : (
                      <p className="profile-empty-text">
                        Noch keine Wünsche – passende Jobs zeigen wir dir dann weiter oben.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Arbeitgeber: Firmenbeschreibung */}
              {!isYouth && (
                <div className="profile-section">
                  <div className="profile-section__header">
                    <div className="profile-section__title"><Star size={16} /> Über mich</div>
                    {!editing && (
                      <button className="profile-section__edit" onClick={() => setEditing(true)}>
                        <Edit3 size={14} /> Bearbeiten
                      </button>
                    )}
                  </div>
                  {editing ? (
                    <textarea
                      className="profile-textarea"
                      value={profileData.bio}
                      onChange={e => setProfileData({ ...profileData, bio: e.target.value })}
                      rows={4}
                      placeholder="Beschreibe dich kurz..."
                      maxLength={500}
                    />
                  ) : (
                    <p className="profile-text">{profileData.bio || <span className="profile-empty-text">Noch keine Beschreibung hinzugefügt.</span>}</p>
                  )}
                </div>
              )}

              {/* Kontakt */}
              <div className="profile-section">
                <div className="profile-section__header">
                  <div className="profile-section__title"><Mail size={16} /> Kontakt</div>
                </div>
                <div className="profile-contact-list">
                  <div className="profile-contact-item">
                    <Mail size={15} /> <span>{user.email}</span>
                  </div>
                  {editing ? (
                    <>
                      <LocationInput
                        value={profileData.location}
                        onChange={(text, loc) =>
                          setProfileData({ ...profileData, location: text, location_id: loc?.id ?? null })
                        }
                        inputClassName="profile-input"
                        placeholder="Standort (z.B. Kaiserslautern)"
                      />
                      <input
                        type="tel"
                        className="profile-input"
                        value={profileData.phone}
                        onChange={e => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="Telefon"
                        style={{ marginTop: 8 }}
                      />
                    </>
                  ) : (
                    <>
                      {profileData.location && <div className="profile-contact-item"><MapPin size={15} /> <span>{profileData.location}</span></div>}
                      {profileData.phone && <div className="profile-contact-item"><Phone size={15} /> <span>{profileData.phone}</span></div>}
                    </>
                  )}
                </div>
              </div>

              {/* Save / Cancel buttons */}
              {editing && (
                <div className="profile-edit-actions">
                  <button className="profile-edit-cancel" onClick={() => { setEditing(false); setSaveError(null); }}>
                    Abbrechen
                  </button>
                  <button className="profile-edit-save" onClick={handleSave} disabled={saveLoading}>
                    {saveLoading ? <><Loader size={15} className="spin" /> Speichern...</> : 'Speichern'}
                  </button>
                </div>
              )}

              {/* Settings section */}
              <div className="profile-settings">
                <h3 className="profile-settings__title">Einstellungen</h3>
                <div className="profile-settings__list">
                  <button className="profile-settings__item" onClick={() => setEditing(true)}>
                    <div className="profile-settings__icon profile-settings__icon--blue">
                      <Edit3 size={18} />
                    </div>
                    <span>Profil bearbeiten</span>
                    <ChevronRight size={18} className="profile-settings__chevron" />
                  </button>
                  <Link to="/impressum" className="profile-settings__item">
                    <div className="profile-settings__icon profile-settings__icon--gray">
                      <FileText size={18} />
                    </div>
                    <span>Impressum</span>
                    <ChevronRight size={18} className="profile-settings__chevron" />
                  </Link>
                  <Link to="/datenschutz" className="profile-settings__item">
                    <div className="profile-settings__icon profile-settings__icon--gray">
                      <Shield size={18} />
                    </div>
                    <span>Datenschutz</span>
                    <ChevronRight size={18} className="profile-settings__chevron" />
                  </Link>
                  <button className="profile-settings__item profile-settings__item--danger" onClick={handleLogout}>
                    <div className="profile-settings__icon profile-settings__icon--red">
                      <LogOut size={18} />
                    </div>
                    <span>Ausloggen</span>
                    <ChevronRight size={18} className="profile-settings__chevron" />
                  </button>
                  <button
                    className="profile-settings__item profile-settings__item--danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <div className="profile-settings__icon profile-settings__icon--red">
                      <Trash2 size={18} />
                    </div>
                    <span>Account löschen</span>
                    <ChevronRight size={18} className="profile-settings__chevron" />
                  </button>
                </div>
              </div>

              {/* Delete confirmation */}
              {showDeleteConfirm && (
                <div className="profile-delete-confirm">
                  <p className="profile-delete-confirm__text">
                    Bist du sicher? Dein Account und alle Daten werden unwiderruflich gelöscht.
                  </p>
                  <div className="profile-delete-confirm__actions">
                    <button
                      className="profile-delete-confirm__cancel"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleteLoading}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="profile-delete-confirm__confirm"
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? <Loader size={15} className="spin" /> : 'Endgültig löschen'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Saved tab */}
      {activeTab === 'saved' && (
        <div className="profile-jobs-tab">
          {savedJobs.length === 0 ? (
            <div className="profile-jobs-empty">
              <Bookmark size={40} />
              <h3>Keine gespeicherten Jobs</h3>
              <p>Speichere Jobs, die dich interessieren.</p>
              <Link to="/search" className="profile-jobs-empty__btn">Jobs durchsuchen</Link>
            </div>
          ) : (
            savedJobs.map(job => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="profile-job-item">
                <div className="profile-job-logo">{(job.company || 'J').charAt(0)}</div>
                <div className="profile-job-info">
                  <p className="profile-job-title">{jobTitle(job.title)}</p>
                  <p className="profile-job-meta">{job.company} · {job.location}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      )}

      {/* Applied tab */}
      {activeTab === 'applied' && (
        <div className="profile-jobs-tab">
          {appliedJobs.length === 0 ? (
            <div className="profile-jobs-empty">
              <CheckCircle size={40} />
              <h3>Noch keine Bewerbungen</h3>
              <p>Entdecke Jobs und bewirb dich direkt in der App.</p>
              <Link to="/" className="profile-jobs-empty__btn">Jobs entdecken</Link>
            </div>
          ) : (
            appliedJobs.map(job => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="profile-job-item">
                <div className="profile-job-logo">{(job.company || 'J').charAt(0)}</div>
                <div className="profile-job-info">
                  <p className="profile-job-title">{jobTitle(job.title)}</p>
                  <p className="profile-job-meta">{job.company} · {job.location}</p>
                </div>
                <span className={`profile-status profile-status--${job.status || 'pending'}`}>
                  {job.status === 'accepted' ? 'Angenommen'
                    : job.status === 'invited' ? 'Eingeladen'
                    : job.status === 'shortlisted' ? 'Vorgemerkt'
                    : job.status === 'viewed' ? 'Gesehen'
                    : job.status === 'rejected' ? 'Abgelehnt'
                    : 'Ausstehend'}
                </span>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
